<script setup lang="ts">
import { api } from '@/api';
import { type ChangePasswordInput, ChangePasswordInputTypeEnum } from '@/api/generator';
import PasswordField from '@/components/forms/PasswordField/PasswordField.vue';
import VeePasswordField from '@/components/forms/PasswordField/VeePasswordField.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { edit } from '@/components/icons/icons.ts';
import PasswordQualityEstimation from '@/components/PasswordQualityEstimation/PasswordQualityEstimation.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { useZxcvbn } from '@/plugins/zxcvbn';
import { useAccountStore } from '@/stores/accountStore.ts';
import { useNotificationStore } from '@/stores/notificationStore';
import { truthy } from '@/utils/helper';
import { passwordMinLength } from '@/validation/patches/patches';
import { useTranslation } from 'i18next-vue';
import { pick } from 'lodash';
import { useFieldValue } from 'vee-validate';
import { computed, ref } from 'vue';

const { t } = useTranslation();
const accountStore = useAccountStore();

const initialValues: Partial<ChangePasswordInput> = {
  $type:
    ChangePasswordInputTypeEnum.PtwPtw100AppZeroApplicationSharedAuthorizationAccountDtoChangePasswordInputPtwPtw100AppZeroApplicationShared,
};

const { form } = useVeeForm<ChangePasswordInput>({
  schemaName: 'ChangePasswordInput',
  options: {
    initialValues,
  },
  mode: 'edit',
});

const { handleSubmit, meta } = form;

const editMode = ref(false);
const password = useFieldValue<string>('newPassword');

const notificationStore = useNotificationStore();

const userInputs = computed(() =>
  Object.values(
    pick(accountStore.currentUser, [
      'iuid',
      'userName',
      'firstName',
      'middleName',
      'lastName',
      'emailAddress',
    ]),
  )
    .filter(truthy)
    .map(String),
);

const zxcvbnResult = useZxcvbn(password, userInputs);

const hasWarningNotification = computed(() => {
  return Boolean(zxcvbnResult.value.feedback.warning);
});

const showPasswordLengthHint = computed(() => {
  return (password.value?.length || 0) < passwordMinLength;
});

const submit = handleSubmit(async (changePasswordInput) => {
  const { success } = await api.account.changePassword(
    {
      changePasswordInput,
    },
    [formBackendErrorHandler(form)],
  );

  if (success) {
    notificationStore.success(t('PasswordChangedMsg'));
    form.resetForm();
    editMode.value = false;
  }
});
</script>

<template>
  <p class="text-h5 mb-6">
    {{ $t('Password') }}
    <TextVBtn
      color="secondary"
      size="small"
      variant="text"
      :icon="edit"
      :active="editMode"
      @click="editMode = !editMode"
    />
  </p>
  <form @submit="submit">
    <input
      v-if="accountStore.currentUser"
      id="username"
      v-model="accountStore.currentUser.emailAddress"
      autocomplete="username"
      type="email"
      class="d-none"
    />

    <VeePasswordField
      v-if="editMode"
      name="currentPassword"
      :label="$t('CurrentPassword')"
      :placeholder="$t('Required')"
    />
    <PasswordField
      v-else
      :label="$t('CurrentPassword')"
      type="password"
      value="****************"
      disabled
    />
    <VeePasswordField
      v-if="editMode"
      name="newPassword"
      class="mt-2"
      :hint="showPasswordLengthHint ? $t('PasswordComplexityViolationMsg') : ''"
      :persistentHint="true"
      :label="$t('NewPassword')"
      :placeholder="$t('Required')"
      autocomplete="new-password"
    />
    <PasswordField
      v-else
      class="mt-2"
      :label="$t('NewPassword')"
      type="password"
      value="****************"
      disabled
      :hint="$t('PasswordComplexityViolationMsg')"
      :persistentHint="true"
    />
    <PasswordQualityEstimation :result="zxcvbnResult" />
    <div v-if="editMode" class="text-right">
      <VeeSubmitButton
        :disabled="!meta.valid || hasWarningNotification"
        :text="$t('ChangePassword')"
        class="mt-6"
      />
    </div>
  </form>
</template>
